import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const API = `${BACKEND_URL}/api`;

const ScriptsPage = () => {
  const [newScript, setNewScript] = useState({ name: '', content: '', system_prompt: '', faq_list: [] });
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingScript, setEditingScript] = useState({ id: '', name: '', content: '', system_prompt: '', faq_list: [] });
  const [loading, setLoading] = useState(false);
  const [faqBulkText, setFaqBulkText] = useState('');

  // Utiliser currentScript pour l'édition
  const currentScript = isEditing ? editingScript : newScript;
  const setCurrentScript = isEditing ? setEditingScript : setNewScript;

  // Use infinite scroll for scripts
  const {
    data: scriptsData,
    loading: loadingScripts,
    hasMore: hasMoreScripts,
    observerTarget: scriptsObserverTarget,
    reset: resetScripts
  } = useInfiniteScroll(
    async (page, limit) => {
      const response = await axios.get(`${API}/scripts`, { params: { page, limit } });
      return response;
    },
    {
      initialPage: 1,
      pageSize: 20,
      enabled: true
    }
  );

  const scripts = scriptsData || [];

  const loadScripts = async () => {
    // Scripts are loaded via infinite scroll hook
    resetScripts();
  };

  const createScript = async () => {
    if (!newScript.name || !newScript.content) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API}/scripts`, {
        name: newScript.name,
        content: newScript.content,
        system_prompt: newScript.system_prompt || '',
        faq_list: (newScript.faq_list || []).filter(f => f.question && f.answer)
      });
      toast.success('Script créé avec succès');
      setNewScript({ name: '', content: '', system_prompt: '', faq_list: [] });
      setFaqBulkText('');
      setIsCreating(false);
      loadScripts();
    } catch (error) {
      console.error('Error creating script:', error);
      toast.error('Erreur lors de la création du script');
    } finally {
      setLoading(false);
    }
  };

  const updateScript = async () => {
    if (!editingScript.name || !editingScript.content) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      setLoading(true);
      await axios.put(`${API}/scripts/${editingScript.id}`, {
        name: editingScript.name,
        content: editingScript.content,
        system_prompt: editingScript.system_prompt || '',
        faq_list: (editingScript.faq_list || []).filter(f => f.question && f.answer)
      });
      toast.success('Script modifié avec succès');
      setIsEditing(false);
      setIsCreating(false);
      setEditingScript(null);
      loadScripts();
    } catch (error) {
      console.error('Error updating script:', error);
      toast.error('Erreur lors de la modification du script');
    } finally {
      setLoading(false);
    }
  };

  const deleteScript = async (scriptId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce script ?')) {
      return;
    }

    try {
      await axios.delete(`${API}/scripts/${scriptId}`);
      toast.success('Script supprimé avec succès');
      loadScripts();
    } catch (error) {
      console.error('Error deleting script:', error);
      toast.error('Erreur lors de la suppression du script');
    }
  };

  const startEditing = (script) => {
    console.log('🔧 Démarrage édition script:', script.name);
    setEditingScript({
      id: script._id,
      name: script.name,
      content: script.content,
      system_prompt: script.system_prompt || '',
      faq_list: script.faq_list || []
    });
    // Rebuild bulk text from existing FAQ entries
    const bulkText = (script.faq_list || []).map(f => `Q: ${f.question}\nA: ${f.answer}`).join('\n\n');
    setFaqBulkText(bulkText);
    setIsEditing(true);
    setIsCreating(true); // Afficher le formulaire d'édition
    console.log('🔧 États mis à jour - isEditing:', true, 'isCreating:', true);
  };

  const insertTag = (tag) => {
    const textarea = document.getElementById('script-content');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = textarea.value;
      const before = text.substring(0, start);
      const after = text.substring(end, text.length);
      const newText = before + tag + after;

      if (isEditing) {
        setEditingScript({ ...editingScript, content: newText });
      } else {
        setNewScript({ ...newScript, content: newText });
      }

      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + tag.length, start + tag.length);
      }, 0);
    }
  };

  const renderScriptModal = () => {
    const currentScript = isEditing ? editingScript : newScript;
    const setCurrentScript = isEditing ? setEditingScript : setNewScript;
    const handleSubmit = isEditing ? updateScript : createScript;
    const title = isEditing ? 'Modifier le Script' : 'Créer un Nouveau Script';

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
          <h2 className="text-2xl font-bold mb-6">{title}</h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom du Script *
                </label>
                <input
                  type="text"
                  value={currentScript.name}
                  onChange={(e) => setCurrentScript({ ...currentScript, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Nom du script..."
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags Conversationnels
                </label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: 'Pause d\'écoute', tag: '##' },
                    { label: 'Question ouverte', tag: '\n\nQUESTION: ' },
                    { label: 'Objection', tag: '\n\nOBJECTION: ' },
                    { label: 'Closing', tag: '\n\nCLOSING: ' },
                    { label: 'Prise de rendez-vous', tag: '\n\nRDV: ' },
                    { label: 'Rappel', tag: '\n\nRAPPEL: ' }
                  ].map((item) => (
                    <button
                      key={item.label}
                      onClick={() => insertTag(item.tag)}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-gray-200 transition-colors"
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contenu du Script *
                </label>
                <textarea
                  id="script-content"
                  value={currentScript.content}
                  onChange={(e) => setCurrentScript({ ...currentScript, content: e.target.value })}
                  rows={12}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Écrivez votre script ici... Utilisez ## pour les pauses d'écoute"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prompt Système IA
                </label>
                <textarea
                  value={currentScript.system_prompt}
                  onChange={(e) => setCurrentScript({ ...currentScript, system_prompt: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Instructions pour l'IA concernant le comportement et la personnalité à adopter..."
                />
              </div>

              {/* FAQ / Objection Handling - Bulk Paste */}
              <div className="mb-4 border-t pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ❓ FAQ / Gestion des Objections
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Collez toutes vos questions-réponses d'un coup au format <strong>Q:</strong> / <strong>A:</strong> ci-dessous, puis cliquez "Analyser".
                </p>
                <textarea
                  value={faqBulkText}
                  onChange={(e) => setFaqBulkText(e.target.value)}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  placeholder={`Q: Vous appelez de la part de qui ?\nA: Nous sommes un service d'accompagnement pour les aides.\n\nQ: C'est quoi le but de cet appel ?\nA: Je vous appelle pour vérifier votre éligibilité.\n\nQ: Est-ce gratuit ?\nA: Oui, le diagnostic est entièrement gratuit.`}
                />
                <div className="flex items-center gap-3 mt-2">
                  <button
                    type="button"
                    onClick={() => {
                      // Parse Q:/A: format
                      const pairs = [];
                      const blocks = faqBulkText.split(/\n\s*\n/); // Split by blank lines
                      blocks.forEach(block => {
                        const lines = block.trim().split('\n');
                        let question = '';
                        let answer = '';
                        lines.forEach(line => {
                          const trimmed = line.trim();
                          if (trimmed.match(/^Q\s*[:：]\s*/i)) {
                            question = trimmed.replace(/^Q\s*[:：]\s*/i, '').trim();
                          } else if (trimmed.match(/^A\s*[:：]\s*/i)) {
                            answer = trimmed.replace(/^A\s*[:：]\s*/i, '').trim();
                          }
                        });
                        if (question && answer) {
                          pairs.push({ question, answer });
                        }
                      });
                      if (pairs.length > 0) {
                        setCurrentScript({ ...currentScript, faq_list: pairs });
                        toast.success(`✅ ${pairs.length} FAQ entries parsed successfully`);
                      } else {
                        toast.error('No valid Q:/A: pairs found. Use format: Q: question\\nA: answer');
                      }
                    }}
                    className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors"
                  >
                    📋 Analyser ({faqBulkText.split(/\n\s*\n/).filter(b => b.trim()).length || 0} blocs)
                  </button>
                  {(currentScript.faq_list || []).length > 0 && (
                    <span className="text-sm text-green-600 font-medium">
                      ✅ {currentScript.faq_list.filter(f => f.question && f.answer).length} FAQ enregistrées
                    </span>
                  )}
                  {(currentScript.faq_list || []).length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setCurrentScript({ ...currentScript, faq_list: [] });
                        setFaqBulkText('');
                        toast.info('FAQ list cleared');
                      }}
                      className="px-3 py-1.5 text-red-600 border border-red-300 rounded-lg text-sm hover:bg-red-50 transition-colors"
                    >
                      🗑️ Tout effacer
                    </button>
                  )}
                </div>

                {/* Parsed FAQ entries preview */}
                {(currentScript.faq_list || []).length > 0 && (
                  <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
                    {currentScript.faq_list.map((faq, index) => (
                      <div key={index} className="flex items-start gap-2 p-2 bg-gray-50 rounded-lg border text-sm">
                        <span className="text-gray-400 font-mono text-xs mt-1">{index + 1}</span>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-800 truncate">Q: {faq.question}</div>
                          <div className="text-gray-600 truncate">A: {faq.answer}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const updatedList = [...currentScript.faq_list];
                            updatedList.splice(index, 1);
                            setCurrentScript({ ...currentScript, faq_list: updatedList });
                            // Rebuild bulk text
                            const newBulkText = updatedList.map(f => `Q: ${f.question}\nA: ${f.answer}`).join('\n\n');
                            setFaqBulkText(newBulkText);
                          }}
                          className="text-red-400 hover:text-red-600 text-xs"
                          title="Supprimer"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => {
                    setIsCreating(false);
                    setIsEditing(false);
                    setEditingScript(null);
                    setNewScript({ name: '', content: '', system_prompt: '', faq_list: [] });
                    setFaqBulkText('');
                  }}
                  className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {loading ? (isEditing ? 'Modification...' : 'Création...') : (isEditing ? 'Modifier le Script' : 'Créer le Script')}
                </button>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-3">Aperçu du Script</h3>
              <div className="bg-white rounded-lg p-3 border max-h-96 overflow-y-auto">
                <pre className="whitespace-pre-wrap text-sm text-gray-700">
                  {currentScript.content || 'Commencez à écrire votre script...'}
                </pre>
              </div>

              {currentScript.system_prompt && (
                <div className="mt-4">
                  <h4 className="font-medium text-gray-900 mb-2">Prompt Système</h4>
                  <div className="bg-blue-50 rounded-lg p-3 border">
                    <pre className="whitespace-pre-wrap text-sm text-blue-700">
                      {currentScript.system_prompt}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Gestion des Scripts Conversationnels</h1>

        <div className="mb-6">
          <button
            onClick={() => setIsCreating(true)}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            📝 Nouveau Script
          </button>
        </div>

        {(isCreating || isEditing) && renderScriptModal()}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Scripts Sauvegardés</h2>
            <div className="space-y-4">
              {scripts.map((script) => (
                <div key={script._id} className="bg-white rounded-lg shadow-md p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-medium text-gray-900">{script.name}</h3>
                    {/* // <span className="text-xs text-gray-500">{new Date(script.created_at).toLocaleDateString('fr-FR')}</span> */}
                  </div>
                  <div className="text-sm text-gray-600 mb-3">
                    {script.content.substring(0, 150)}...
                  </div>
                  {script.system_prompt && (
                    <div className="text-xs text-blue-600 mb-3">
                      💡 Prompt système configuré
                    </div>
                  )}
                  {script.faq_list && script.faq_list.length > 0 && (
                    <div className="text-xs text-green-600 mb-3">
                      ❓ {script.faq_list.length} FAQ configurées
                    </div>
                  )}
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={() => startEditing(script)}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      Modifier
                    </button>
                    <button
                      onClick={() => deleteScript(script._id)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              ))}
              
              {/* Infinite scroll observer target */}
              {hasMoreScripts && (
                <div ref={scriptsObserverTarget} className="h-10 flex items-center justify-center py-4">
                  {loadingScripts && <div className="text-gray-500">Loading more scripts...</div>}
                </div>
              )}
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="font-medium text-blue-900 mb-3">📋 Guide des Scripts</h3>
            <div className="space-y-3 text-sm text-blue-800">
              <div>
                <strong>## Pause d'écoute</strong>
                <p>Utilisez ## pour que l'IA fasse une pause et écoute la réponse du prospect</p>
              </div>
              <div>
                <strong>Questions ouvertes</strong>
                <p>Posez des questions qui nécessitent une réponse développée</p>
              </div>
              <div>
                <strong>Prompt système</strong>
                <p>Définit le comportement et la personnalité de l'IA</p>
              </div>
              <div>
                <strong>Gestion des objections</strong>
                <p>Préparez des réponses aux objections courantes</p>
              </div>
              <div>
                <strong>Closing</strong>
                <p>Finalisez avec un appel à l'action clair</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScriptsPage;